/*
 *   Copyright (c) 2025 2023 Celadon Development LLC
 *   All rights reserved.
 *   Author Aliaksandr Ziabko <aliaksandr.ziabko@celadonsoft.com>
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Formik, Form, Field } from "formik";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import Header from "../../components/Header";
import UploadButton from "../../components/UploadButton";
import BackButton from "../../components/BackButton";
import SuccessMessage from "../../components/SuccessMessage";
import ErrorMessage from "../../components/ErrorMessage";

import {
  createHowToPerform,
  uploadHtpMedia,
  getHtpCategories,
  HtpCategory,
} from "../../services/howToPerformService";

interface Video {
  url: string;
  thumbnail?: string;
}

interface FormValues {
  name: string;
  description: string;
  categoriesIds: string[];
  imageUrls: string[];
  videoUrls: Video[];
}

const CreateHowToPerform: React.FC = () => {
  const navigate = useNavigate();

  const [categories, setCategories] = useState<HtpCategory[]>([]);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /* ================= FETCH CATEGORIES ================= */
  useEffect(() => {
    getHtpCategories().then((cats) =>
      setCategories(Array.isArray(cats) ? cats : [cats])
    );
  }, []);

  const initialValues: FormValues = {
    name: "",
    description: "",
    categoriesIds: [],
    imageUrls: [],
    videoUrls: [],
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (values: FormValues) => {
    if (values.videoUrls.length < 1) {
      setError("Video is requite");
      return;
    }
    try {
      await createHowToPerform({
        name: values.name,
        description: values.description,
        categoriesIds: values.categoriesIds.map(Number),
        imageUrls: values.imageUrls,
        videoUrls: values.videoUrls,
      });

      setSuccess("Tutorials created successfully");
      setTimeout(() => navigate("/htp"), 1500);
    } catch (err: any) {
      console.log(err);
      setError(
        err && err.response
          ? err.response.data.message.includes("duplicate")
            ? "Name must be unique"
            : err.response.data.message
          : "Failed to create Tutorials"
      );
    }
  };

  /* ================= MEDIA ================= */
  const handleUpload = async (
    file: File,
    type: "image" | "video",
    setFieldValue: (field: string, value: any) => void,
    values: FormValues
  ): Promise<string | null> => {
    try {
      const res = await uploadHtpMedia(0, file);
      if (type === "image") {
        const oldImage = values.imageUrls?.[0] ?? null;

        const newImages = [res.url, ...(oldImage ? [oldImage] : [])];

        setFieldValue("imageUrls", newImages);
        return res.url;
      } else {
        /* ===== VIDEO ===== */
        const oldVideo = values.videoUrls?.[0] ?? null;

        const newVideos = [
          {
            url: res.url,
            thumbnail: res.thumbnail,
          },
          ...(oldVideo ? [oldVideo] : []),
        ];

        setFieldValue("videoUrls", newVideos);
        return res.url;
      }
    } catch (err: any) {
      setError(err);
      return null;
    }
  };

  const handleImageRemove = (
    index: number,
    setFieldValue: (field: string, value: any) => void,
    values: FormValues
  ) => {
    const newImages = [...values.imageUrls];
    newImages.splice(index, 1);
    setFieldValue("imageUrls", newImages);
  };

  const handleVideoRemove = (
    setFieldValue: (field: string, value: any) => void
  ) => {
    setFieldValue("videoUrls", []);
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <Header title="Create Trainer Tutorials" />

      {success && (
        <SuccessMessage message={success} onClose={() => setSuccess(null)} />
      )}
      {error && <ErrorMessage message={error} onClose={() => setError(null)} />}

      <BackButton onClick={() => navigate("/htp")} />

      <Formik initialValues={initialValues} onSubmit={handleSubmit}>
        {({ values, setFieldValue }) => (
          <Form className="mt-6 bg-white p-6 rounded shadow border">
            {/* NAME */}
            <div className="mb-4">
              <label className="block text-sm font-medium">Name</label>
              <Field name="name" className="mt-1 w-full border rounded p-2" />
            </div>

            {/* DESCRIPTION */}
            <div className="mb-4">
              <label className="block text-sm font-medium">Description</label>
              <ReactQuill
                value={values.description}
                onChange={(v) => setFieldValue("description", v)}
              />
            </div>

            {/* CATEGORIES */}
            <div className="mb-4">
              <label className="block text-sm font-medium">Categories</label>
              <select
                className="w-full border rounded p-2"
                onChange={(e) =>
                  e.target.value &&
                  !values.categoriesIds.includes(e.target.value) &&
                  setFieldValue("categoriesIds", [
                    ...values.categoriesIds,
                    e.target.value,
                  ])
                }
              >
                <option value="">Select category</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>

              <div className="flex gap-2 mt-2 flex-wrap">
                {values.categoriesIds.map((id) => {
                  const cat = categories.find((c) => c.id.toString() === id);
                  return (
                    <span key={id} className="bg-gray-100 px-2 py-1 rounded">
                      {cat?.name}
                      <button
                        className="ml-2 text-red-500"
                        type="button"
                        onClick={() =>
                          setFieldValue(
                            "categoriesIds",
                            values.categoriesIds.filter((c) => c !== id)
                          )
                        }
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
            </div>

            {/* MEDIA */}
            <div className="flex gap-4">
              {/* IMAGES */}
              <div className="flex flex-col items-center">
                <UploadButton
                  index={0}
                  onRemove={(index) =>
                    handleImageRemove(index, setFieldValue, values)
                  }
                  label="Upload Image"
                  accept="image/*"
                  onUpload={async (file) =>
                    await handleUpload(file!, "image", setFieldValue, values)
                  }
                />
                {values.imageUrls.map((url, i) => (
                  <div key={i} className="mt-2 relative">
                    <img
                      src={url}
                      alt="Preview"
                      className="w-24 h-24 object-cover border rounded"
                    />
                  </div>
                ))}
              </div>

              {/* VIDEOS */}
              <div className="flex flex-col items-center">
                <UploadButton
                  index={1}
                  onRemove={() => handleVideoRemove(setFieldValue)}
                  label="Upload Video"
                  accept="video/*"
                  onUpload={async (file) =>
                    await handleUpload(file!, "video", setFieldValue, values)
                  }
                />
                {values.videoUrls.map((video, i) => (
                  <div key={i} className="mt-2 relative">
                    {video.thumbnail ? (
                      <img
                        src={video.thumbnail}
                        alt="Preview"
                        className="w-24 h-24 object-cover border rounded"
                      />
                    ) : (
                      <span className="block w-24 h-24 border rounded bg-gray-200 text-center text-xs">
                        No thumbnail
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-4">
              <button
                type="button"
                onClick={() => navigate("/how-to-perform")}
                className="px-4 py-2 bg-gray-500 text-white rounded"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-green-600 text-white rounded"
              >
                Create
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default CreateHowToPerform;
